import {
  alpha,
  AppBar,
  Box,
  ClickAwayListener,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Slide,
  Stack,
  styled,
  Theme,
  Toolbar,
  Typography,
} from '@mui/material';
import Person from '@mui/icons-material/Person';
import React, { useMemo, useRef, useState } from 'react';
import { NavigationButton } from './NavigationButton';
import { NavigationSearchBar } from './NavigationSearchBar';
import { ChevronLeft, ChevronRight, Logout, OpenInNew, Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useEkko from '../Providers/EkkoProvider';
import usePagination from '../Providers/PaginationProvider';
import useDataTable from '../Providers/DataTableProvider';
import { useSearchBar } from '../Stores/SearchBarProvider';
import { UserAvatar } from './UserAvatar';

const HeaderBar = styled(AppBar)({
  zIndex: 0,
  backgroundColor: 'transparent',
  height: '100%',
  width: '100%',
  animationTimingFunction: 'ease',
  animationName: '$headerNonSticky',
  animationDuration: '500ms',
  '@keyframes headerSticky': {
    '0%': {
      top: -100,
    },
    '100%': {
      top: 0,
    },
  },
  '@keyframes headerNonSticky': {
    '0%': {
      top: 170,
      zIndex: 9999,
    },
    '100%': {
      top: 0,
      zIndex: 0,
    },
  },
});

const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, 250, 500];

const paginationChipSx = (theme: Theme) => ({
  color: theme.palette.header.contrastText,
  backgroundColor: alpha(theme.palette.header.contrastText, 0.12),
  '&:hover': { backgroundColor: alpha(theme.palette.header.contrastText, 0.2) },
  '&.Mui-disabled': {
    color: alpha(theme.palette.header.contrastText, 0.35),
    backgroundColor: alpha(theme.palette.header.contrastText, 0.06),
  },
});

interface Props {
  title: string;
  sticky: boolean;
}

export const Header = ({ title, sticky }: Props): React.JSX.Element => {
  const { api, currentUser, logOut, config } = useEkko();
  const navigate = useNavigate();
  const { page, limit, setPage, setLimit } = usePagination();
  const { headerTitle, isInputDisabled, isHidden } = useSearchBar();
  const { data, totalItemCount } = useDataTable();

  // Show pagination on list/table pages (search bar visible) whenever there
  // are results — kept visible even on a single page so the rows-per-page
  // selector stays usable; prev/next disable themselves at the bounds.
  // Mirrors how the search bar gates itself via `isHidden`.
  const showPagination = !isHidden && totalItemCount > 0;

  const popperRef = useRef();
  const [openPopper, setOpenPopper] = useState<undefined | 'profile'>();

  const username = useMemo(() => currentUser?.username?.toUpperCase() ?? '', [currentUser?.username]);

  const popperHead = useMemo(
    () => (
      <MenuItem disabled>
        <Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent={'center'}>
          <UserAvatar username={username} size="medium" sx={{ outline: 1 }} />
          <Typography fontWeight={700}>{username}</Typography>
        </Stack>
      </MenuItem>
    ),
    [username],
  );

  const popperElement = useMemo(() => {
    const {
      workerUrl,
      api: { url },
    } = config;
    const apiUrl = url + '/doc/public';

    return (
      <Popper
        transition
        open={openPopper !== undefined}
        anchorEl={popperRef.current}
        disablePortal={sticky}
        style={{ zIndex: 6000 }}
        placement={'bottom-start'}
      >
        {({ TransitionProps, placement }) => (
          <Slide
            {...TransitionProps}
            direction={'left'}
            style={{ transformOrigin: placement === 'bottom-start' ? 'top' : 'bottom' }}
          >
            <Paper sx={{ zIndex: 5000 }}>
              <ClickAwayListener onClickAway={() => setOpenPopper(undefined)}>
                <MenuList>
                  {popperHead}
                  <MenuItem divider disabled />
                  {workerUrl && (
                    <MenuItem
                      component={'a'}
                      href={workerUrl}
                      target={'_blank'}
                      rel={'noopener noreferrer'}
                      onClick={() => setOpenPopper(undefined)}
                    >
                      <ListItemIcon>
                        <OpenInNew />
                      </ListItemIcon>
                      <ListItemText>{'Shopfloor App'}</ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem
                    component={'a'}
                    href={apiUrl}
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    onClick={() => setOpenPopper(undefined)}
                  >
                    <ListItemIcon>
                      <OpenInNew />
                    </ListItemIcon>
                    <ListItemText>{'API Documentation'}</ListItemText>
                  </MenuItem>
                  <MenuItem divider disabled />
                  <MenuItem
                    onClick={() => {
                      setOpenPopper(undefined);
                      navigate('/me/');
                    }}
                  >
                    <ListItemIcon>
                      <Settings />
                    </ListItemIcon>
                    <ListItemText>{'Settings'}</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setOpenPopper(undefined);
                      logOut();
                      navigate('/login');
                    }}
                  >
                    <ListItemIcon>
                      <Logout />
                    </ListItemIcon>
                    <ListItemText>{'Logout'}</ListItemText>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Slide>
        )}
      </Popper>
    );
  }, [api, config, openPopper, popperHead, navigate, logOut]);

  const paginationElement = useMemo(() => {
    const rowsPerPage = limit ?? 10;
    const consolidatedPage = data.length === 0 ? 0 : (page ?? 1) - 1;
    const pageCount = Math.max(1, Math.ceil(totalItemCount / rowsPerPage));
    const from = totalItemCount === 0 ? 0 : consolidatedPage * rowsPerPage + 1;
    const to = Math.min((consolidatedPage + 1) * rowsPerPage, totalItemCount);
    const atFirst = consolidatedPage === 0;
    const atLast = consolidatedPage >= pageCount - 1;

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          size="small"
          aria-label="Previous Page"
          disabled={isInputDisabled || atFirst}
          onClick={() => setPage(consolidatedPage)}
          sx={paginationChipSx}
        >
          <ChevronLeft />
        </IconButton>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            height: 40,
            borderRadius: 48,
            color: theme.palette.header.contrastText,
            backgroundColor: alpha(theme.palette.header.contrastText, 0.12),
            fontVariantNumeric: 'tabular-nums',
          })}
        >
          <Select
            value={rowsPerPage}
            onChange={(e) => setLimit(Number(e.target.value))}
            variant="standard"
            disableUnderline
            disabled={isInputDisabled}
            sx={{
              color: 'inherit',
              '.MuiSelect-select': { py: 0, pr: '20px !important', pl: 0 },
              '.MuiSelect-select:focus': { backgroundColor: 'transparent' },
              '.MuiSelect-icon': { color: 'inherit' },
            }}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {from}–{to} of {totalItemCount}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Next Page"
          disabled={isInputDisabled || atLast}
          onClick={() => setPage(consolidatedPage + 2)}
          sx={paginationChipSx}
        >
          <ChevronRight />
        </IconButton>
      </Stack>
    );
  }, [totalItemCount, data, page, limit, isInputDisabled, setPage, setLimit]);

  const titleStack = useMemo(
    () => (
      <Stack direction={'column'} spacing={0} alignItems={'flex-start'} justifyContent={'center'}>
        <Typography noWrap flex={1} component={'div'} variant={'h6'} sx={{ color: 'header.contrastText' }}>
          {title}
        </Typography>
        {headerTitle && (
          <Typography noWrap flex={1} component={'div'} fontWeight={700} sx={{ color: 'header.contrastText' }}>
            {headerTitle}
          </Typography>
        )}
      </Stack>
    ),
    [title, headerTitle],
  );

  const controlStack = useMemo(
    () => (
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={{ xs: 1.5, lg: 2 }}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        justifyContent={{ lg: 'space-between' }}
        width="100%"
      >
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: { lg: 420 } }}>
          <NavigationSearchBar sticky={sticky} />
        </Box>
        <Slide in={showPagination} direction={'down'} mountOnEnter unmountOnExit>
          <Box sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', lg: 'auto' } }}>{paginationElement}</Box>
        </Slide>
      </Stack>
    ),
    [sticky, paginationElement, showPagination],
  );

  return (
    <>
      {popperElement}
      <Box
        component={'div'}
        width={'100%'}
        sx={{
          // Content below has a fixed marginTop: -14 (-112px) that overlaps
          // the bottom of the header. Header height must leave enough visible
          // area above that overlap to fit the Toolbar, which is taller below
          // the lg breakpoint because controls stack vertically.
          height: { xs: 320, lg: 220 },
          backgroundColor: 'header.main',
        }}
      >
        <HeaderBar
          // elevation={0} disables AppBar's default white-tint background
          // overlay (MUI applies it at any elevation > 0 in dark mode). Without
          // this, the AppBar paints a translucent veil over its full-viewport
          // extent (it's position: fixed + 100% width/height) that bleeds onto
          // any in-flow content below — the dark band we want comes from the
          // wrapping Box's `header.main`, not from AppBar elevation.
          elevation={0}
          sx={{
            boxShadow: 'none',
            position: 'relative',
            paddingTop: 1,
            ...(sticky && {
              paddingTop: 0,
              position: 'fixed',
              zIndex: 100,
              top: 0,
              right: 3,
              height: 'auto',
              width: 'auto',
              animationName: '$headerSticky',
            }),
          }}
        >
          <Toolbar
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', lg: 'auto minmax(0, 1fr) auto' },
              gridTemplateAreas: {
                xs: `"title profile" "controls controls"`,
                lg: `"title controls profile"`,
              },
              alignItems: 'center',
              columnGap: 2,
              rowGap: { xs: 1.5, lg: 2 },
              py: 1,
              px: { xs: 2, lg: 3 },
            }}
          >
            <Box sx={{ gridArea: 'title', minWidth: 0, width: { lg: 260 } }}>{titleStack}</Box>
            <Box sx={{ gridArea: 'controls', minWidth: 0 }}>{controlStack}</Box>
            <Box ref={popperRef} sx={{ gridArea: 'profile' }}>
              <NavigationButton icon={Person} sticky={sticky} onClick={() => setOpenPopper('profile')} />
            </Box>
          </Toolbar>
        </HeaderBar>
      </Box>
    </>
  );
};
